import { Clock } from "lucide-react"
import { Card } from "@/components/ui/Card"
import { Button } from "@/components/ui/Button"
import AssignRoleDialog from "./AssignRoleDialog"
import RejectRequestDialog from "./RejectRequestDialog"
import type { RoleCard } from "@/components/RoleCardList"
import type { OrgTreeNode } from "@/lib/orgTree"

export type PendingRequest = {
  id: string
  org_node_id: string
  org_node_path: string
  requested_role: { id: string; name: string } | null
  role_note: string
  submitted_at: string
}

export type PendingMember = {
  user_id: string
  name: string
  email: string
  request: PendingRequest
}

type Props = {
  pending: PendingMember[]
  roles: RoleCard[]
  nodes: OrgTreeNode[]
}

// Access requests awaiting review. These users are NOT in the members table —
// this section is the only place they exist until an admin decides. Approve
// opens the assign dialog prefilled with what was requested (still editable —
// the grant IS the approval); Reject is explicit.
export default function PendingRequestsSection({ pending, roles, nodes }: Props) {
  return (
    <Card className="overflow-hidden border-warning/40">
      <div className="flex items-center gap-2 border-b border-border bg-warning-surface/40 px-4 py-2.5">
        <Clock size={15} className="text-warning" />
        <h2 className="text-sm font-medium text-foreground">Waiting for approval</h2>
        <span className="text-sm text-muted-foreground">· {pending.length}</span>
      </div>

      <ul className="divide-y divide-border">
        {pending.map((m) => (
          <li key={m.request.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
            <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-medium text-primary">
              {m.name.charAt(0).toUpperCase()}
            </span>

            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-baseline gap-x-2">
                <span className="text-sm font-medium text-foreground">{m.name}</span>
                <span className="text-xs text-muted-foreground">{m.email}</span>
              </div>
              <div className="text-sm text-muted-foreground">
                <span className="font-medium text-foreground">
                  {m.request.requested_role?.name ?? `“${m.request.role_note}”`}
                </span>{" "}
                · {m.request.org_node_path}
              </div>
              <div className="text-xs text-muted-foreground">
                {m.request.requested_role && <>“{m.request.role_note}” · </>}
                {m.request.submitted_at.slice(0, 10)}
              </div>
            </div>

            <div className="flex shrink-0 items-center gap-1">
              <RejectRequestDialog requestId={m.request.id} userName={m.name} />
              <AssignRoleDialog
                userId={m.user_id}
                roles={roles}
                nodes={nodes}
                existing={[]}
                defaultRoleId={m.request.requested_role?.id ?? null}
                defaultNodeId={m.request.org_node_id}
                trigger={
                  <Button size="sm">
                    {m.request.requested_role ? "Approve as requested" : "Assign role…"}
                  </Button>
                }
              />
            </div>
          </li>
        ))}
      </ul>
    </Card>
  )
}
